import React, { useCallback, useEffect, useState } from 'react';
import { IoMdAddCircleOutline, IoMdAdd, IoMdSearch } from 'react-icons/io';
import { TextField, Grid } from '@mui/material';
import { Dialog, toast } from '@cincoders/cinnamon';
import ParamBox from '../../components/ParamBox';
import { ProcessTypeService } from '../../services/ProcessTypeService';
import { Flow, IconButton, ParamButton, ParamList } from './styles';
import { PageForm, FormButton } from '../../components/FormLayout/FormLayout.styles';
import { Parameter, ProcessType } from '../../types/Process.d';
import 'react-toastify/dist/ReactToastify.css';
import { showError, NOT_FOUND } from '../../utils/showError';

interface EditParameter extends Parameter {
  identifier?: number;
}

interface ProcessTypeOverrided extends Omit<ProcessType, 'parameters'> {
  parameters: EditParameter[];
}

const initialTypeProcess: ProcessType = {
  name: '',
  averageTime: 0,
  timeLimit: 0,
  parameters: [],
};

const ProcessTypeForm: React.FC = () => {
  const [processTypeValue, setTypeProcess] = useState(initialTypeProcess);
  const [isEditing, setIsEditing] = useState<boolean>();
  const [isSearching, setIsSearching] = useState(true);
  const [title, setTitle] = useState<string>('Tipo de Processo');
  const [oldProcessTypeName, setOldProcessTypeName] = useState<string>('');
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [newSearch, setNewSearch] = useState<boolean>();

  useEffect(() => {
    if (isEditing === true) {
      setTitle('Editar Tipo de Processo');
    } else if (isEditing === false) {
      setTitle('Criar Tipo de Processo');
    }
  }, [isEditing]);

  function onChangeTypeName(event: React.ChangeEvent<HTMLInputElement>) {
    if (isEditing) {
      setNewSearch(true);
      setOldProcessTypeName(processTypeValue.name);
      setDialogVisibility();
    }

    processTypeValue.name = event.target.value;
    setTypeProcess({ ...processTypeValue });
  }

  function onChangeAverageTime(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    const convertedAverageTime = parseInt(value, 10);
    processTypeValue.averageTime = convertedAverageTime;
    setTypeProcess({ ...processTypeValue });
  }

  function onChangeTimeLimit(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    const convertedTimeLimit = parseInt(value, 10);
    processTypeValue.timeLimit = convertedTimeLimit;
    setTypeProcess({ ...processTypeValue });
  }

  const onChangeParam = useCallback(() => {
    setTypeProcess({ ...processTypeValue });
  }, [processTypeValue]);

  const removeParamfromList = useCallback(
    (paramIdentifier: number) => {
      for (let i = 0; i < processTypeValue.parameters.length; i += 1) {
        if (processTypeValue.parameters[i].id === paramIdentifier) {
          const index = i;
          processTypeValue.parameters.splice(index, 1);
        }
      }
    },
    [processTypeValue.parameters],
  );
  const onDeleteParam = useCallback(
    // a exclusão de um parâmetro no modo edição
    // precisa fazer a requisição pro back antes de o fazer na tela
    async (parameter: EditParameter) => {
      if (isEditing && parameter.id) {
        const response = await ProcessTypeService.deleteParameterById(parameter.id);
        if (response.status === 200 && parameter.identifier) {
          removeParamfromList(parameter.identifier);
          setTypeProcess({ ...processTypeValue });
        } else {
          toast.error('Você está tentando excluir um parâmetro que já está associado à um fluxo.');
        }
      } else if (parameter.identifier) {
        // no modo criação só é preciso excluir o parâmetro aqui no front mesmo
        removeParamfromList(parameter.identifier);
        setTypeProcess({ ...processTypeValue });
      }
    },
    [isEditing, processTypeValue, removeParamfromList],
  );

  function isProcessTypeValidated(processType: ProcessTypeOverrided) {
    if (processType.name === '' || processType.name === ' ') {
      return false;
    }
    if (processType.averageTime <= 0 && processType.averageTime !== 0) {
      return false;
    }
    for (let i = 0; i < processType.parameters.length; i += 1) {
      if (processType.parameters[i].abbreviation === '' || processType.parameters[i].abbreviation === ' ') {
        return false;
      }
      if (processType.parameters[i].description === '' || processType.parameters[i].description === ' ') {
        return false;
      }
    }
    return true;
  }

  function clearForm() {
    setTypeProcess(state => ({
      id: state.id,
      name: processTypeValue.name,
      averageTime: 0,
      timeLimit: 0,
      parameters: [],
    }));
  }

  async function enableSearchMode() {
    searchTypeProcess();
  }

  async function enableCreationMode() {
    if (processTypeValue.name !== '') {
      ProcessTypeService.getTypeProcessByName(processTypeValue.name)
        .then(response => {
          toast.error(
            'Você não pode adicionar este Tipo de Processo porque ele já existe. ' +
              'Por favor, edite o mesmo ou cadastre um tipo processo com outro nome.',
          );
          const loadedTypeProcess: ProcessTypeOverrided = response.data;
          setIsEditing(true);
          setIsSearching(false);
          setTypeProcess({ ...loadedTypeProcess });
        })
        .catch(error => {
          if (error.response) {
            if (error.response.status === NOT_FOUND) {
              setIsSearching(false);
              setIsEditing(false);
              clearForm();
            } else {
              showError(error.response.status);
            }
          } else if (error.request) {
            showError();
          } else {
            showError();
          }
        });
    } else {
      toast.error('É necessário inserir o nome do Tipo de Processo. Preencha os campos corretamente!');
    }
  }

  function searchTypeProcess() {
    if (processTypeValue.name !== '') {
      ProcessTypeService.getTypeProcessByName(processTypeValue.name)
        .then(response => {
          const loadedTypeProcess: ProcessTypeOverrided = response.data;
          if (!isEditing || newSearch) {
            loadedTypeProcess.averageTime /= 1440; // 24*60 conversão h e min
            loadedTypeProcess.timeLimit /= 1440;
            setNewSearch(false);
            setIsEditing(true);
            setIsSearching(false);
            setTypeProcess({ ...loadedTypeProcess });
          } else {
            processTypeValue.id = loadedTypeProcess.id;
            processTypeValue.name = loadedTypeProcess.name;
          }
        })
        .catch(error => {
          if (error.response) {
            if (error.response.status === NOT_FOUND) {
              toast.info(
                'Esse Tipo de Processo não existe. Crie um novo Tipo de Processo ou ' +
                  'pesquise novamente por um Tipo de Processo existente.',
              );
              setIsSearching(true);
              setIsEditing(false);
              clearForm();
            } else {
              showError(error.response.status);
            }
          } else if (error.request) {
            showError();
          } else {
            showError();
          }
        });
    } else {
      toast.error('É necessário inserir o nome do Tipo de Processo. Preencha os campos corretamente!');
    }
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (isEditing) {
      searchTypeProcess();
      const finalParameters = processTypeValue.parameters.map(param => ({
        id: param.id === -1 ? undefined : param.id,
        abbreviation: param.abbreviation,
        description: param.description,
        type: param.type,
      }));
      processTypeValue.parameters = finalParameters;

      if (isProcessTypeValidated(processTypeValue)) {
        ProcessTypeService.putTypeProcess({
          id: processTypeValue.id,
          name: processTypeValue.name,
          averageTime: processTypeValue.averageTime * 24 * 60,
          timeLimit: processTypeValue.timeLimit * 24 * 60,
          parameters: processTypeValue.parameters,
        })
          .then(() => {
            setTypeProcess({ ...processTypeValue });
            toast.success('Tipo de Processo editado com sucesso!');
          })
          .catch(error => {
            if (error.response) {
              showError(error.status.response);
            } else if (error.request) {
              showError();
            } else {
              showError();
            }
          });
      } else {
        toast.error('Por favor, preencha todos os campos corretamente!');
      }
    } else {
      const finalParameters = processTypeValue.parameters.map(param => ({
        abbreviation: param.abbreviation,
        description: param.description,
        type: param.type,
      }));
      processTypeValue.parameters = finalParameters;

      if (isProcessTypeValidated(processTypeValue)) {
        ProcessTypeService.postTypeProcess({
          name: processTypeValue.name,
          averageTime: processTypeValue.averageTime * 24 * 60,
          timeLimit: processTypeValue.timeLimit * 24 * 60,
          parameters: processTypeValue.parameters,
        })
          .then(() => {
            toast.success('Tipo de Processo criado com sucesso!');
            setIsEditing(true);
          })
          .catch(error => {
            if (error.response) {
              showError(error.response.status);
            } else if (error.request) {
              showError();
            } else {
              showError();
            }
          });
      } else {
        toast.error('Por favor, preencha todos os campos corretamente!');
      }
    }
  }

  function handleKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      searchTypeProcess();
    }
  }

  function addParamBox() {
    if (processTypeValue.parameters.length > 0) {
      const parameter: EditParameter = {
        id: -1,
        // Fazendo com o paramIdentifier do novo parâmetro sempre
        // seja um número a mais do que o paramIdentifier do último parâmetro da lista
        identifier: processTypeValue.parameters[processTypeValue.parameters.length - 1].id
          ? processTypeValue.parameters[processTypeValue.parameters.length - 1].id! + 1
          : 1,
        abbreviation: '',
        description: '',
        type: 'boolean',
      };
      processTypeValue.parameters.push(parameter);
    } else {
      const parameter: EditParameter = {
        id: -1,
        // Fazendo com o paramIdentifier do novo parâmetro sempre
        // seja um número a mais do que o paramIdentifier do último parâmetro da lista
        identifier: 1,
        abbreviation: '',
        description: '',
        type: 'boolean',
      };
      processTypeValue.parameters.push(parameter);
    }
    setTypeProcess({ ...processTypeValue });
  }

  function setDialogVisibility() {
    setIsDialogOpen(true);
  }

  function handleDialogAccept() {
    setIsDialogOpen(false);
    searchTypeProcess();
  }

  function handleDialogReject() {
    setNewSearch(false);
    setIsDialogOpen(false);
    processTypeValue.name = oldProcessTypeName;
  }

  return (
    <>
      <Dialog
        type='decision'
        title='Deseja descartar as alterações?'
        visibility={isDialogOpen}
        setVisibility={setDialogVisibility}
        acceptLabel='Sim'
        rejectLabel='Não'
        acceptFunction={handleDialogAccept}
        rejectFunction={handleDialogReject}
      >
        Você alterou o nome do Tipo de Processo. Caso deseje manter as alterações atuais, selecione `Não´, caso você
        selecione `Sim´ iremos pesquisar pelo Tipo de Processo correspondente ao novo nome inserido.
      </Dialog>
      <PageForm>
        <h2>{title}</h2>
        <form onSubmit={onSubmit}>
          <Grid container marginTop='1em' marginBottom='1em'>
            <Grid item lg md xs>
              <TextField
                fullWidth
                label='Nome'
                id='typeName'
                name='typeName'
                type='text'
                onKeyPress={handleKeyPress}
                onChange={onChangeTypeName}
                value={processTypeValue.name}
                required
              />
            </Grid>
            <IconButton onClick={enableSearchMode} type='button'>
              <IoMdSearch size={25} />
            </IconButton>
            <IconButton onClick={enableCreationMode} type='button'>
              <IoMdAdd size={25} />
            </IconButton>
          </Grid>
          <TextField
            fullWidth
            label='Tempo Médio (em dias)'
            id='typeAverageTime'
            name='typeAverageTime'
            type='number'
            disabled={isSearching}
            onChange={onChangeAverageTime}
            value={processTypeValue.averageTime || ''}
            required
          />
          <TextField
            fullWidth
            style={{ marginTop: '1em' }}
            label='Tempo Limite (em dias)'
            id='typeTimeLimit'
            name='typeTimeLimit'
            type='number'
            disabled={isSearching}
            onChange={onChangeTimeLimit}
            value={processTypeValue.timeLimit || ''}
            required
          />
          <ParamList>
            <span>Parâmetros</span>
            {processTypeValue.parameters.map(param => (
              <ParamBox key={param.id} parameter={param} onChange={onChangeParam} onDelete={onDeleteParam} />
            ))}

            <ParamButton>
              <IconButton onClick={addParamBox} type='button' disabled={isSearching}>
                {isSearching ? <IoMdAddCircleOutline size={25} /> : <IoMdAddCircleOutline size={25} />}
              </IconButton>
            </ParamButton>
          </ParamList>

          <span>Fluxo</span>
          <Flow />

          <hr />
          <FormButton variant='contained' type='button' disabled={isSearching} onClick={onSubmit}>
            Salvar
          </FormButton>
        </form>
      </PageForm>
    </>
  );
};

export default ProcessTypeForm;
